import { call, put, select, takeEvery } from 'redux-saga/effects';
import { SendApiRequest } from 'src/api';
import { apiRoutes } from 'src/api/routes';
import {
    fetchCompanies,
    companiesFetched,
    companiesFetchedFailed,
    fetchSingleCompany,
    singleCompanyFetched,
    singleCompanyFetchFailed,
    updateCompany,
    companyUpdated,
    companyUpdateFailed,
    updateSkillRates,
    skillRatesUpdated,
    skillRatesUpdateFailed,
    updateExcludedSubSkills,
    excludedSubSkillsUpdated,
    excludedSubSkillsUpdateFailed,
    addUnitNumber,
    unitNumberAdded,
    unitNumberAddFailed,
    deleteUnitNumber,
    unitNumberDeleted,
    unitNumberDeleteFailed,
    addHomecareTask,
    homecareTaskAdded,
    homecareTaskAddFailed,
    updateHomecareTask,
    homecareTaskUpdated,
    homecareTaskUpdateFailed,
    deleteHomecareTask,
    homecareTaskDeleted,
    homecareTaskDeleteFailed,
    addHomecarePatient,
    homecarePatientAdded,
    homecarePatientAddFailed,
    updateHomecarePatient,
    homecarePatientUpdated,
    homecarePatientUpdateFailed,
    deleteHomecarePatient,
    homecarePatientDeleted,
    homecarePatientDeleteFailed,
    updateBulletins,
    bulletinsUpdated,
    bulletinsUpdateFailed,
    getCompanyId,
    getSingleCompany,
    // Schedule presets
    fetchSchedulePresets,
    schedulePresetsFetched,
    schedulePresetsFetchFailed,
    updateSchedulePresets,
    schedulePresetsUpdated,
    schedulePresetsUpdateFailed,
    // Unit persons
    updateUnitPersons,
    unitPersonsUpdated,
    unitPersonsUpdateFailed,
    // Company logo
    setCompanyLogoUrl,
    updateCompanyLogo,
    companyLogoUpdated,
    companyLogoUpdateFailed,
    // Contacts
    // Block list
    blockStaffer,
    stafferBlocked,
    stafferBlockFailed,
    unblockStaffer,
    stafferUnblocked,
    stafferUnblockFailed,
    // Secondary logins
    updateSecondaryLogins,
    secondaryLoginsUpdated,
    secondaryLoginsUpdateFailed,
    // Email categories
    updateEmailCategories,
    emailCategoriesUpdated,
    emailCategoriesUpdateFailed,
    // Password / notification / delete
    changeCompanyPassword,
    companyPasswordChanged,
    companyPasswordChangeFailed,
    resetCompanyPassword,
    companyPasswordReset,
    companyPasswordResetFailed,
    sendCompanyNotification,
    companyNotificationSent,
    companyNotificationSendFailed,
    deleteCompany,
    companyDeleted,
    companyDeleteFailed,
} from '../company';
import { showToastAction } from '../ui';

// Helper to get creator ID from company
function getCreatorId(company) {
    if (!company) return null;
    return company.creator?._id || company.creator;
}

function* fetchCompaniesEffect() {
    try {
        const response = yield call(SendApiRequest, {
            url: apiRoutes.entities,
            method: 'GET',
            queryParams: {},
        });
        yield put(
            companiesFetched({
                results: response.data,
            }),
        );
    } catch (e) {
        yield put(companiesFetchedFailed());
    }
}

function* fetchSingleCompanyEffect() {
    try {
        const response = yield call(SendApiRequest, {
            url: apiRoutes.entities,
            method: 'GET',
            queryParams: {},
        });
        const company = response.data[0];
        if (company) {
            yield put(singleCompanyFetched(company));
            // Fetch the company user (role: "user") to get the actual company logo.
            // entity.creator is the admin in whitelabel mode, not the company logo holder.
            try {
                const usersResponse = yield call(SendApiRequest, {
                    url: apiRoutes.users,
                    method: 'GET',
                    queryParams: { entityId: company._id, role: 'user' },
                });
                const users = usersResponse.results || usersResponse.data || [];
                const companyUser = Array.isArray(users) ? users[0] : null;
                const profileUrl = companyUser?.profileUrl;
                const logoUrl =
                    typeof profileUrl === 'string'
                        ? profileUrl
                        : profileUrl?.secure_url || profileUrl?.url || null;
                if (logoUrl) {
                    yield put(setCompanyLogoUrl(logoUrl));
                }
            } catch (_) {
                // Non-critical: logo just won't show
            }
        } else {
            yield put(singleCompanyFetchFailed());
        }
    } catch (e) {
        yield put(singleCompanyFetchFailed());
    }
}

function* updateCompanyEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const companyId = yield select(getCompanyId);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.updateEntity(companyId),
            method: 'PUT',
            body: data,
        });
        yield put(companyUpdated(response.data || response));
        yield put(
            showToastAction({
                type: 'success',
                text: 'Company updated successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(companyUpdateFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to update company',
            }),
        );
        yield call(reject, e);
    }
}

function* updateSkillRatesEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const companyId = yield select(getCompanyId);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.customizeSkillRates(companyId),
            method: 'POST',
            body: data,
        });
        yield put(skillRatesUpdated({ rates: data.rates || data }));
        yield put(
            showToastAction({
                type: 'success',
                text: 'Skill rates updated successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(skillRatesUpdateFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to update skill rates',
            }),
        );
        yield call(reject, e);
    }
}

function* updateExcludedSubSkillsEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const companyId = yield select(getCompanyId);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.excludeSubSkills(companyId),
            method: 'POST',
            body: data,
        });
        yield put(
            excludedSubSkillsUpdated({
                excludedSubSkills: data.excludedSubSkills,
            }),
        );
        yield put(
            showToastAction({
                type: 'success',
                text: 'Excluded sub-skills updated successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(excludedSubSkillsUpdateFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to update excluded sub-skills',
            }),
        );
        yield call(reject, e);
    }
}

function* addUnitNumberEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const companyId = yield select(getCompanyId);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.entityUnitNumbers(companyId),
            method: 'POST',
            body: data,
        });
        yield put(
            unitNumberAdded({
                unitNumber: response.data?.unitNumber || response.unitNumber,
            }),
        );
        yield put(
            showToastAction({
                type: 'success',
                text: 'Unit number added successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(unitNumberAddFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to add unit number',
            }),
        );
        yield call(reject, e);
    }
}

function* deleteUnitNumberEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const companyId = yield select(getCompanyId);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.entityUnitNumbers(companyId),
            method: 'DELETE',
            body: data,
        });
        yield put(
            unitNumberDeleted({
                unitNumber: response.data?.unitNumber || response.unitNumber,
            }),
        );
        yield put(
            showToastAction({
                type: 'success',
                text: 'Unit number deleted successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(unitNumberDeleteFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to delete unit number',
            }),
        );
        yield call(reject, e);
    }
}

function* addHomecareTaskEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const companyId = yield select(getCompanyId);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.entityTasks(companyId),
            method: 'POST',
            body: data,
        });
        yield put(
            homecareTaskAdded({
                tasks: response.data?.tasks || response.tasks,
            }),
        );
        yield put(
            showToastAction({
                type: 'success',
                text: 'Task added successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(homecareTaskAddFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to add task',
            }),
        );
        yield call(reject, e);
    }
}

function* updateHomecareTaskEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const companyId = yield select(getCompanyId);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.entityTaskById(companyId, data.taskId),
            method: 'PUT',
            body: data,
        });
        yield put(
            homecareTaskUpdated({
                tasks: response.data?.tasks || response.tasks,
            }),
        );
        yield put(
            showToastAction({
                type: 'success',
                text: 'Task updated successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(homecareTaskUpdateFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to update task',
            }),
        );
        yield call(reject, e);
    }
}

function* deleteHomecareTaskEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const companyId = yield select(getCompanyId);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.entityTaskById(companyId, data.taskId),
            method: 'DELETE',
        });
        yield put(
            homecareTaskDeleted({
                tasks: response.data?.tasks || response.tasks,
            }),
        );
        yield put(
            showToastAction({
                type: 'success',
                text: 'Task deleted successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(homecareTaskDeleteFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to delete task',
            }),
        );
        yield call(reject, e);
    }
}

function* addHomecarePatientEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const companyId = yield select(getCompanyId);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.entityPatients(companyId),
            method: 'POST',
            body: data,
        });
        yield put(
            homecarePatientAdded({
                patients: response.data?.patients || response.patients,
            }),
        );
        yield put(
            showToastAction({
                type: 'success',
                text: 'Patient added successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(homecarePatientAddFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to add patient',
            }),
        );
        yield call(reject, e);
    }
}

function* updateHomecarePatientEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const companyId = yield select(getCompanyId);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.entityPatientById(companyId, data.patientId),
            method: 'PUT',
            body: data,
        });
        yield put(
            homecarePatientUpdated({
                patients: response.data?.patients || response.patients,
            }),
        );
        yield put(
            showToastAction({
                type: 'success',
                text: 'Patient updated successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(homecarePatientUpdateFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to update patient',
            }),
        );
        yield call(reject, e);
    }
}

function* deleteHomecarePatientEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const companyId = yield select(getCompanyId);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.entityPatientById(companyId, data.patientId),
            method: 'DELETE',
        });
        yield put(
            homecarePatientDeleted({
                patients: response.data?.patients || response.patients,
            }),
        );
        yield put(
            showToastAction({
                type: 'success',
                text: 'Patient deleted successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(homecarePatientDeleteFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to delete patient',
            }),
        );
        yield call(reject, e);
    }
}

function* updateBulletinsEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const companyId = yield select(getCompanyId);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.entityBulletins(companyId),
            method: 'PUT',
            body: data,
        });
        yield put(
            bulletinsUpdated({
                bulletins: response.data?.bulletins || response.bulletins,
            }),
        );
        yield put(
            showToastAction({
                type: 'success',
                text: 'Bulletins updated successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(bulletinsUpdateFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to update bulletins',
            }),
        );
        yield call(reject, e);
    }
}

// ---- NEW SAGA EFFECTS ----

function* fetchSchedulePresetsEffect(action) {
    const {
        payload: { resolve, reject },
    } = action;
    try {
        const company = yield select(getSingleCompany);
        const creatorId = getCreatorId(company);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.schedulePresets,
            method: 'GET',
            queryParams: { entityId: creatorId },
        });
        const presets = response.data || response;
        yield put(
            schedulePresetsFetched(Array.isArray(presets) ? presets : []),
        );
        yield call(resolve, presets);
    } catch (e) {
        yield put(schedulePresetsFetchFailed());
        yield call(reject, e);
    }
}

function* updateSchedulePresetsEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const company = yield select(getSingleCompany);
        const creatorId = getCreatorId(company);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.schedulePresetsBulk,
            method: 'POST',
            body: { entity: creatorId, presets: data.presets },
        });
        const presets = response.data || response;
        yield put(
            schedulePresetsUpdated(Array.isArray(presets) ? presets : []),
        );
        yield put(
            showToastAction({
                type: 'success',
                text: 'Schedule presets updated successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(schedulePresetsUpdateFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to update schedule presets',
            }),
        );
        yield call(reject, e);
    }
}

function* blockStafferEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const company = yield select(getSingleCompany);
        const creatorId = getCreatorId(company);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.blockStafferFromCompany(creatorId),
            method: 'POST',
            body: { blockedId: data.blockedId, type: 'admin' },
        });
        const blockedList =
            response.data?.blocked_list || response.blocked_list || [];
        yield put(stafferBlocked(blockedList));
        yield put(
            showToastAction({
                type: 'success',
                text: 'Staffer blocked successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(stafferBlockFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to block staffer',
            }),
        );
        yield call(reject, e);
    }
}

function* unblockStafferEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const company = yield select(getSingleCompany);
        const creatorId = getCreatorId(company);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.unblockStafferFromCompany(creatorId),
            method: 'POST',
            body: { blockedId: data.blockedId, type: 'admin' },
        });
        const blockedList =
            response.data?.blocked_list || response.blocked_list || [];
        yield put(stafferUnblocked(blockedList));
        yield put(
            showToastAction({
                type: 'success',
                text: 'Staffer unblocked successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(stafferUnblockFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to unblock staffer',
            }),
        );
        yield call(reject, e);
    }
}

function* updateUnitPersonsEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const companyId = yield select(getCompanyId);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.unitPersons(companyId),
            method: 'PUT',
            body: data,
        });
        const unitPersons =
            response.data?.unitPersons || response.unitPersons || data;
        yield put(unitPersonsUpdated(unitPersons));
        yield put(
            showToastAction({
                type: 'success',
                text: 'Unit managers updated successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(unitPersonsUpdateFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to update unit managers',
            }),
        );
        yield call(reject, e);
    }
}

function* updateCompanyLogoEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        // data is { formData, userId } — upload to the company user, not the admin
        const targetUserId = data.userId;
        if (!targetUserId) {
            throw new Error('Company user ID is required for logo upload');
        }
        const response = yield call(SendApiRequest, {
            url: apiRoutes.updateUserPicture(targetUserId),
            method: 'PUT',
            body: data.formData,
        });
        const profileUrl = response.data;
        yield put(companyLogoUpdated(profileUrl));
        yield put(
            showToastAction({
                type: 'success',
                text: 'Company logo updated successfully',
            }),
        );
        yield call(resolve, profileUrl);
    } catch (e) {
        yield put(companyLogoUpdateFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to update company logo',
            }),
        );
        yield call(reject, e);
    }
}

function* updateSecondaryLoginsEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const company = yield select(getSingleCompany);
        const creatorId = getCreatorId(company);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.secondaryLogins(creatorId),
            method: 'PUT',
            body: data,
        });
        const logins =
            response.data?.secondaryLogins || response.secondaryLogins || [];
        yield put(secondaryLoginsUpdated(logins));
        yield put(
            showToastAction({
                type: 'success',
                text: 'Secondary logins updated successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(secondaryLoginsUpdateFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to update secondary logins',
            }),
        );
        yield call(reject, e);
    }
}

function* updateEmailCategoriesEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const company = yield select(getSingleCompany);
        const creatorId = getCreatorId(company);
        const response = yield call(SendApiRequest, {
            url: apiRoutes.updateUser(creatorId),
            method: 'PUT',
            body: {
                otherEmailsWithCategories: data.otherEmailsWithCategories,
            },
        });
        const categories =
            response.data?.otherEmailsWithCategories ||
            response.otherEmailsWithCategories ||
            data.otherEmailsWithCategories;
        yield put(emailCategoriesUpdated(categories));
        yield put(
            showToastAction({
                type: 'success',
                text: 'Email categories updated successfully',
            }),
        );
        yield call(resolve, response);
    } catch (e) {
        yield put(emailCategoriesUpdateFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to update email categories',
            }),
        );
        yield call(reject, e);
    }
}

function* changeCompanyPasswordEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const company = yield select(getSingleCompany);
        const creatorId = getCreatorId(company);
        yield call(SendApiRequest, {
            url: apiRoutes.updateUser(creatorId),
            method: 'POST',
            body: { password: data.password },
        });
        yield put(companyPasswordChanged());
        yield put(
            showToastAction({
                type: 'success',
                text: 'Password changed successfully',
            }),
        );
        yield call(resolve);
    } catch (e) {
        yield put(companyPasswordChangeFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to change password',
            }),
        );
        yield call(reject, e);
    }
}

function* resetCompanyPasswordEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        yield call(SendApiRequest, {
            url: apiRoutes.sendResetPasswordEmail,
            method: 'POST',
            body: { email: data.email },
        });
        yield put(companyPasswordReset());
        yield put(
            showToastAction({
                type: 'success',
                text: 'Password reset email sent successfully',
            }),
        );
        yield call(resolve);
    } catch (e) {
        yield put(companyPasswordResetFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to send password reset email',
            }),
        );
        yield call(reject, e);
    }
}

function* sendCompanyNotificationEffect(action) {
    const {
        payload: { data, resolve, reject },
    } = action;
    try {
        const company = yield select(getSingleCompany);
        const creatorId = getCreatorId(company);
        yield call(SendApiRequest, {
            url: apiRoutes.sendNotification,
            method: 'POST',
            body: { users: [creatorId], message: data.message },
        });
        yield put(companyNotificationSent());
        yield put(
            showToastAction({
                type: 'success',
                text: 'Notification sent successfully',
            }),
        );
        yield call(resolve);
    } catch (e) {
        yield put(companyNotificationSendFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to send notification',
            }),
        );
        yield call(reject, e);
    }
}

function* deleteCompanyEffect(action) {
    const {
        payload: { resolve, reject },
    } = action;
    try {
        const company = yield select(getSingleCompany);
        const creatorId = getCreatorId(company);
        yield call(SendApiRequest, {
            url: apiRoutes.markDeleteUser(creatorId),
            method: 'DELETE',
        });
        yield put(companyDeleted());
        yield put(
            showToastAction({
                type: 'success',
                text: 'Company deleted successfully',
            }),
        );
        yield call(resolve);
    } catch (e) {
        yield put(companyDeleteFailed());
        yield put(
            showToastAction({
                type: 'danger',
                text: e.message || 'Failed to delete company',
            }),
        );
        yield call(reject, e);
    }
}

export const companiesSagas = [
    takeEvery(fetchCompanies.type, fetchCompaniesEffect),
    takeEvery(fetchSingleCompany.type, fetchSingleCompanyEffect),
    takeEvery(updateCompany.type, updateCompanyEffect),
    takeEvery(updateSkillRates.type, updateSkillRatesEffect),
    takeEvery(updateExcludedSubSkills.type, updateExcludedSubSkillsEffect),
    takeEvery(addUnitNumber.type, addUnitNumberEffect),
    takeEvery(deleteUnitNumber.type, deleteUnitNumberEffect),
    takeEvery(addHomecareTask.type, addHomecareTaskEffect),
    takeEvery(updateHomecareTask.type, updateHomecareTaskEffect),
    takeEvery(deleteHomecareTask.type, deleteHomecareTaskEffect),
    takeEvery(addHomecarePatient.type, addHomecarePatientEffect),
    takeEvery(updateHomecarePatient.type, updateHomecarePatientEffect),
    takeEvery(deleteHomecarePatient.type, deleteHomecarePatientEffect),
    takeEvery(updateBulletins.type, updateBulletinsEffect),
    // New saga effects
    takeEvery(fetchSchedulePresets.type, fetchSchedulePresetsEffect),
    takeEvery(updateSchedulePresets.type, updateSchedulePresetsEffect),
    takeEvery(blockStaffer.type, blockStafferEffect),
    takeEvery(unblockStaffer.type, unblockStafferEffect),
    takeEvery(updateUnitPersons.type, updateUnitPersonsEffect),
    takeEvery(updateCompanyLogo.type, updateCompanyLogoEffect),
    takeEvery(updateSecondaryLogins.type, updateSecondaryLoginsEffect),
    takeEvery(updateEmailCategories.type, updateEmailCategoriesEffect),
    takeEvery(changeCompanyPassword.type, changeCompanyPasswordEffect),
    takeEvery(resetCompanyPassword.type, resetCompanyPasswordEffect),
    takeEvery(sendCompanyNotification.type, sendCompanyNotificationEffect),
    takeEvery(deleteCompany.type, deleteCompanyEffect),
];
